import type {
  deputy_v1_messaging_Conversation,
  deputy_v1_messaging_Message,
  deputy_v1_messaging_MessageFeedback,
  deputy_v1_messaging_MessagePayload,
  deputy_v1_messaging_MessagePayloadMedia,
  deputy_v1_messaging_Participant,
} from "@deputy-ai/data-platform-api/v1/messaging";
import type { Merge, OverrideProperties, SetRequired } from "type-fest";

export enum MessagingParticipantType {
  Visitor = "visitor",
  AIAgent = "ai_agent",
  HumanAgent = "human_agent",
  System = "system",
}

export enum MessagingEventType {
  CustomerMessage = "customer_message",
  InternalNote = "internal_note",
  SuggestionV2 = "suggestionv2",
  Debug = "debug",
  ContextSplit = "context_split",
  AIToolCall = "ai_call_tool",
  MetricBehavior = "metric_behavior",
  MetricConsumptionTime = "metric_consumption_time",
}

export enum MessagingMessageType {
  Text = "text",
  Image = "image",
  Template = "template",
  File = "file",
  Carousel = "carousel",
  Location = "location",
  List = "list",
  Form = "form",
  FormResponse = "formResponse",
}

export enum MessagingChannelType {
  Chat = "chat",
  Email = "email",
  Web = "web",
}

export enum MessagingStatus {
  Sending = "sending",
  Sent = "sent",
  Failed = "failed",
  Pending = "pending",
  Typing = "typing",
  Delivered = "delivered",
  Read = "read",
}

export enum MessageFeedbackType {
  THUMB_UP = "THUMBS_UP",
  THUMB_DOWN = "THUMBS_DOWN",
}

export enum MessageOrder {
  First = "first",
  Last = "last",
  Middle = "middle",
  Sole = "sole",
}

export enum MessagingConversationState {
  CONVERSATION_STATE_UNSPECIFIED = "CONVERSATION_STATE_UNSPECIFIED",
  CONVERSATION_STATE_ACTIVE = "CONVERSATION_STATE_ACTIVE",
  CONVERSATION_STATE_COMPLETED = "CONVERSATION_STATE_COMPLETED",
  CONVERSATION_STATE_DEBUG = "CONVERSATION_STATE_DEBUG",
}

export type MessagingParticipant = OverrideProperties<
  deputy_v1_messaging_Participant,
  {
    type: MessagingParticipantType;
  }
>;

export type MessagingConversation = OverrideProperties<
  SetRequired<deputy_v1_messaging_Conversation, "uid">,
  {
    participants?: MessagingParticipant[];
    state?: MessagingConversationState;
    metadata?: {
      current_integration_agent_id: string;
      current_integration_name: string;
      external_id: string;
      source_id: string;
      [key: string]: unknown;
    };
  }
>;

export type MessagingMessagePayloadMedia =
  deputy_v1_messaging_MessagePayloadMedia & {
    objectName?: string;
  };

export type MessagingPayload = OverrideProperties<
  deputy_v1_messaging_MessagePayload,
  {
    messageType: MessagingMessageType;
    channel: MessagingChannelType;
    media?: MessagingMessagePayloadMedia;
  }
>;

export type MessagingMessage = Merge<
  OverrideProperties<
    SetRequired<deputy_v1_messaging_Message, "uid" | "orgUid" | "eventTime">,
    {
      eventType: MessagingEventType;
      participant: MessagingParticipant;
      payload: MessagingPayload;
    }
  >,
  {
    status?: MessagingStatus;
    createTimeTs?: number;
  }
>;

export function fromMessageFeedback(
  feedback: deputy_v1_messaging_MessageFeedback
): MessageFeedbackType | null {
  if (!feedback.type) {
    return null;
  }
  const feedbackType = feedback.type as any as string | number;
  if (feedbackType === "THUMBS_UP") {
    return MessageFeedbackType.THUMB_UP;
  }
  if (feedbackType === "THUMBS_DOWN") {
    return MessageFeedbackType.THUMB_DOWN;
  }
  if (feedbackType === 1) {
    return MessageFeedbackType.THUMB_UP;
  }
  if (feedbackType === 2) {
    return MessageFeedbackType.THUMB_DOWN;
  }
  return null;
}

export function toMessageFeedback(
  feedbackType: MessageFeedbackType
): deputy_v1_messaging_MessageFeedback {
  switch (feedbackType) {
    case MessageFeedbackType.THUMB_UP:
      return { type: "THUMBS_UP" };
    case MessageFeedbackType.THUMB_DOWN:
      return { type: "THUMBS_DOWN" };
  }
}

export function getVisitor(conversation: MessagingConversation) {
  return getVisitorByParticipants(conversation?.participants);
}

export function getVisitorByParticipants(
  participants?: MessagingParticipant[]
) {
  return participants?.find((p) => p.type === MessagingParticipantType.Visitor);
}

export function getVisitorNameByConversation(
  conversation: MessagingConversation
) {
  return getVisitor(conversation)?.name;
}
